/**
 * StoryboardEditor.js — 分镜大师节点的专用编辑器
 *
 * ══════════════════════════════════════════════════════════════════
 * 在 storyboardMode=true 的图像生成节点里取代原 prompt textarea，
 * 由两列并置构成（左→右）：
 *
 *   1. 宫格区        — 4/9/16/25 格固定（2×2 / 3×3 / 4×4 / 5×5 正方形排列），
 *                       比例联动模型参数 aspectRatio；点格子弹放大编辑窗
 *   2. 分镜文本解析区 — 用户粘贴分镜脚本 → 正则解析 → 自动填入宫格；
 *                       右下角有「分镜大师」按钮（调 LLM 整理/扩写脚本，后台任务化）
 *
 * 生成时（useNodeActions.handleGenerate）会调用 storyboardParser
 * 的 assembleStoryboardPrompt 把基础提示词 + 比例/排列说明 + 各格内容
 * 拼成一段大 prompt，传给模型。
 *
 * 放大编辑窗（FullscreenEditor）：作为控制台内的 absolute 子元素覆盖
 * 整个控制台，天然随画布平移/缩放；工具栏仅含字号预设（H1/H2/H3/正文）。
 *
 * 数据存储在 node.data 上，所有改动 → handleUpdate({ ... }) 持久化。
 *
 * ── 分镜大师（后台任务，2026-06-16 重构）──
 * 点击分镜大师按钮 → POST /api/ai/storyboard-polish（projectId + nodeId）→ 立即返回；
 * 真正生成在服务端后台跑，进度/结果经 WebSocket node-updated 回流到节点 data：
 *   polishing(进行中) / polishStartTime(计时起点) / polishStatus(阶段文字) /
 *   polishError(失败原因) / parseText(增量结果)。
 * 因此刷新、折叠控制台、切标签页、甚至关软件再回来都能恢复并续上，不再丢失。
 * ══════════════════════════════════════════════════════════════════
 */

import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { createPortal } from 'react-dom'; // 🆕 引入 createPortal
import { Plus, X, Maximize2, Trash2, ChevronUp, LayoutGrid, Sparkles, Check, Wand2, Loader2, Heading1, Heading2, Heading3, Pilcrow, Lock, Unlock } from 'lucide-react';
import QuickTooltip from '../components/QuickTooltip';
import VoiceMicButton from '../components/VoiceMicButton';
import MentionDropdown from './MentionDropdown'; // 🆕 引入素材下拉菜单组件
import SlateStoryboardEditor from '../components/SlateStoryboardEditor'; // 🆕 Slate.js 富文本编辑器（分镜专用）
import { slateValueToText } from '../components/slateMentionUtils'; // 🆕 用于全屏编辑器失焦时同步纯文本
import api from '../api';
import { parseStoryboardText, gridCellsToText } from '../storyboardParser';
import { normalizeAsset } from '../editorUtils';

// ──────────────────────────────────────────────────────────────
// 文本规范化：清理特殊字符，确保 textarea 和高亮层完全对齐
// ──────────────────────────────────────────────────────────────
// 问题：复制粘贴的文本可能包含零宽字符、不同换行符、全角空格等，
// 导致高亮层（手动拼接 <span> + '\n'）和 textarea（原生渲染）
// 的换行点和字符宽度不一致，光标位置和文字显示错位。
//
// 解决：在所有文本输入入口（onChange、粘贴、语音输入）统一清理：
//   1. 统一换行符：\r\n → \n
//   2. 去除零宽字符：ZWSP、ZWNJ、ZWJ、Word Joiner
//   3. 去除 BOM 标记
//   4. 全角空格 → 半角空格（可选，分镜文本建议统一）
//   5. 去除其他不可见控制字符（U+0000~U+001F，保留 \n 和 \t）
// ──────────────────────────────────────────────────────────────
function normalizeText(text) {
  if (!text) return text;

  const normalized = text
    // 1. 统一换行符
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    // 2. 去除零宽字符
    .replace(/[​-‍﻿]/g, '') // ZWSP, ZWNJ, ZWJ, BOM
    .replace(/⁠/g, '') // Word Joiner
    // 3. 全角空格 → 半角空格
    .replace(/　/g, ' ')
    // 4. 去除其他不可见控制字符（保留 \n=U+000A 和 \t=U+0009）
    .replace(/[\u0000-\u0008\u000B-\u001F\u007F]/g, '');

  return normalized;

  return normalized;
}

// ── 分镜标题行高亮 ───────────────────────────────────────────────
// 认「行首标签 + 冒号」这类小节头，只把「标签到冒号」这一段染金色（冒号后的正文
// 保持原色），因此同一行里「标签：内容」也能正确高亮。只改颜色不改字重，保证与
// 下方透明 textarea 逐字对齐、换行点一致，不错位。
// 返回该行需要染色的前缀长度（含冒号）；非标题行返回 0。
function storyboardHeadingCut(line) {
  let m;
  // 1) mm:ss 时间段：0:00~0:05：（冒号在时间内部，需匹配到范围之后的终止冒号）
  m = line.match(/^\s*\d{1,2}:\d{2}\s*[\-~～到至]\s*\d{1,2}:\d{2}[^：:\n]*[：:]/);
  if (m) return m[0].length;
  // 2) 秒时间段：0~5秒： / 0秒~5秒：
  m = line.match(/^\s*\d+(?:\.\d+)?\s*[秒s]?\s*[\-~～到至]\s*\d+(?:\.\d+)?\s*[秒s][^：:\n]*[：:]/);
  if (m) return m[0].length;
  // 3) 镜号（可带时间）：第1镜 0~5秒： / 第一镜： / 【第1镜】：
  m = line.match(/^\s*[【《（(]?\s*第?\s*[零一二三四五六七八九十百两\d]+\s*[镜幕场][^：:\n]*[：:]/);
  if (m) return m[0].length;
  // 4) 编号小节：一、摄像机与人物站位： / 3. 画面描述：
  m = line.match(/^\s*[零一二三四五六七八九十百两\d]+\s*[、.．][^：:\n]*[：:]/);
  if (m) return m[0].length;
  // 5) 纯词标签：反向提示词： / 画面描述： / 音效：（2~12 个中文或字母紧跟冒号）
  m = line.match(/^\s*[一-龥A-Za-z·]{2,12}[：:]/);
  if (m) return m[0].length;
  return 0;
}

// 秒数 → 友好时长（与 NodeCommons 的 formatElapsed 一致：Xs / X分X秒）
function formatElapsed(seconds) {
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return secs > 0 ? `${mins}分${secs}秒` : `${mins}分`;
}

// 段落之间插入一个空行（按行拆分→去空→用空行重连）
// 放大编辑器与宫格单格预览共用，保证两处分段观感一致
function withBlankLines(t) {
  if (!t) return t || '';
  return t.split('\n').map(s => s.trim()).filter(Boolean).join('\n\n');
}

// 4/9/16/25 → 列数（正方形排列）
const GRID_COLS = { 4: 2, 9: 3, 16: 4, 25: 5 };

// 可选宫格档位（供切换按钮使用）
const GRID_OPTIONS = [4, 9, 16, 25];

// 比例字符串 ('16:9') → 宽高比数字（用于 CSS aspectRatio 属性）
function parseRatio(ratio) {
  if (!ratio || typeof ratio !== 'string') return '1 / 1';
  const m = ratio.match(/^(\d+)\s*[:：]\s*(\d+)$/);
  if (!m) return '1 / 1';
  return `${m[1]} / ${m[2]}`;
}

// 宫格单元格固定宽度，不随宫格数/比例变化
function cellWidthForGrid(gridType) {
  return 200;
}

// 生成短 ID（只在新增卡片/格子时用）
const genId = () => `s${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

// ══════════════════════════════════════════════════════════════
// 基础提示词卡片
// ══════════════════════════════════════════════════════════════
function BasePromptCard({ block, onEdit, onDelete }) {
  const preview = (block.text || '').trim().slice(0, 30);
  return (
    <div
      onClick={() => onEdit(block)}
      style={{
        position: 'relative',
        minWidth: 110, maxWidth: 160,
        background: 'rgba(59, 130, 246, 0.12)',
        border: '1px solid rgba(59, 130, 246, 0.4)',
        borderRadius: 8,
        padding: '8px 10px',
        cursor: 'pointer',
        transition: 'all 0.15s',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(59, 130, 246, 0.22)'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'rgba(59, 130, 246, 0.12)'; }}
    >
      <div style={{ fontSize: 11, fontWeight: 600, color: '#93c5fd', marginBottom: 2 }}>
        {block.name || '基础提示词'}
      </div>
      <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.65)', lineHeight: 1.3, wordBreak: 'break-all' }}>
        {preview || <span style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.35)' }}>点击编辑…</span>}
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); onDelete(block.id); }}
        title="删除"
        style={{
          position: 'absolute', top: 2, right: 2,
          width: 16, height: 16, padding: 0,
          background: 'rgba(0,0,0,0.4)', border: 'none', borderRadius: '50%',
          color: '#fca5a5', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <X size={10} />
      </button>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// 放大编辑器（给基础提示词 / 宫格单格 / 右侧脚本框共用）
// 🆕 使用 createPortal 渲染到 document.body，实现真正的全屏效果
// 工具栏保留字号预设（H1/H2/H3/正文）+ 语音输入 + 保存按钮
// ══════════════════════════════════════════════════════════════
function FullscreenEditor({ open, title, name, text, onSave, onClose, assets = [] }) {
  // localName 保留原镜名（无输入框，仅透传给 onSave 以免保存时丢失名称）
  const [localName, setLocalName] = useState(name || '');
  const [localText, setLocalText] = useState(normalizeText(text || '')); // 🆕 初始值规范化
  const [localSlateValue, setLocalSlateValue] = useState(null); // 🆕 Slate JSON 字符串
  const [fontSize, setFontSize] = useState(14);   // 字号预设，默认 14（全屏时更大更舒适）
  const [fontWeight, setFontWeight] = useState('normal');
  const textareaRef = useRef(null);
  const editorRef = useRef(null); // 🆕 Slate 编辑器 ref
  const [forceUpdateEditor, setForceUpdateEditor] = useState(0); // 🆕 强制更新编辑器的计数器

  // 每次打开或 text 变化时都同步外部值
  React.useEffect(() => {
    if (open) {
      setLocalName(name || '');
      const normalized = normalizeText(withBlankLines(text || ''));
      setLocalText(normalized);
      setFontSize(14); // 重置字号
      setFontWeight('normal');
      setForceUpdateEditor(prev => prev + 1); // 🔧 强制触发编辑器同步外部值
    }
  }, [open, name, text]);

  // 字号预设（H1=24粗 / H2=20粗 / H3=16粗 / 正文=14常规）
  const setPreset = (type) => {
    switch (type) {
      case 'h1': setFontSize(24); setFontWeight('bold'); break;
      case 'h2': setFontSize(20); setFontWeight('bold'); break;
      case 'h3': setFontSize(16); setFontWeight('bold'); break;
      case 'p': default: setFontSize(14); setFontWeight('normal'); break;
    }
  };

  if (!open) return null;

  const ToolBtn = ({ onClick, title: t, active, children }) => (
    <QuickTooltip content={t} position="top" delay={200}>
      <div onClick={onClick} style={{
        width: 36, height: 36, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer', color: active ? '#60a5fa' : '#cbd5e1',
        background: active ? 'rgba(96,165,250,0.15)' : 'transparent', transition: 'all 0.12s',
      }}
        onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
        onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
      >{children}</div>
    </QuickTooltip>
  );

  // 🆕 使用 createPortal 渲染到 document.body
  return createPortal(
    <div
      className="nodrag nowheel"
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.85)',
        backdropFilter: 'blur(5px)',
        display: 'flex',
        alignItems: 'flex-start', // 顶对齐
        justifyContent: 'center',
        zIndex: 999999,
        paddingTop: '40px', // 距离顶部 40px
        paddingBottom: '450px', // 底部留 450px 给资产列表
        paddingLeft: '20px',
        paddingRight: '20px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '900px',
          maxWidth: '100%',
          height: '80vh',
          background: 'var(--modal-gradient)',
          borderRadius: '20px',
          boxShadow: '0 25px 100px rgba(0,0,0,0.5)',
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid var(--border-subtle)',
          overflow: 'hidden',
        }}
      >
        {/* Header：标题 + 字号工具栏 + 语音 + 保存 */}
        <div style={{
          height: 60,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid var(--border-subtle)',
          background: 'rgba(0,0,0,0.2)',
          borderRadius: '20px 20px 0 0',
          flexShrink: 0,
          padding: '0 20px',
        }}>
          <span style={{ color: 'var(--text-body)', fontSize: 15, fontWeight: 600 }}>{title}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {/* 字号预设工具栏 */}
            <ToolBtn onClick={() => setPreset('h1')} title="标题 1" active={fontSize === 24}><Heading1 size={20} strokeWidth={1.5} /></ToolBtn>
            <ToolBtn onClick={() => setPreset('h2')} title="标题 2" active={fontSize === 20}><Heading2 size={20} strokeWidth={1.5} /></ToolBtn>
            <ToolBtn onClick={() => setPreset('h3')} title="标题 3" active={fontSize === 16}><Heading3 size={20} strokeWidth={1.5} /></ToolBtn>
            <ToolBtn onClick={() => setPreset('p')} title="正文" active={fontSize === 14 && fontWeight === 'normal'}><Pilcrow size={18} strokeWidth={1.5} /></ToolBtn>
            {/* 语音输入 */}
            <VoiceMicButton size={32} position="inline" currentText={localText} onResult={(newText) => setLocalText(normalizeText(newText))} textareaRef={textareaRef} />
            {/* 保存并关闭 */}
            <div
              onClick={() => {
                // 🔧 保存时从编辑器获取最新纯文本
                let finalText = localText;
                if (editorRef.current?.editor) {
                  finalText = slateValueToText(editorRef.current.editor.children);
                }
                onSave(localName, normalizeText(finalText), localSlateValue);
                onClose();
              }}
              title="保存"
              style={{
                width: 36,
                height: 36,
                borderRadius: '50%',
                background: 'var(--bg-hover)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--border-strong)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'var(--bg-hover)'}
            >
              <Check size={20} color="var(--text-primary)" />
            </div>
          </div>
        </div>

        {/* Slate.js 富文本编辑器 */}
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', color: 'var(--text-body)', padding: '40px 60px' }}>
          <SlateStoryboardEditor
            value={localText} // 🔧 使用实时更新的 localText state
            onChange={(e) => {
              // 🔧 只保存 slateValue，纯文本在失焦或保存时再同步
              setLocalSlateValue(e.target.slateValue);
            }}
            onBlur={(e) => {
              // 🔧 失焦时同步纯文本（用于语音输入等场景）
              if (editorRef.current?.editor) {
                const text = slateValueToText(editorRef.current.editor.children);
                setLocalText(normalizeText(text));
              }
            }}
            placeholder="输入提示词内容…"
            assets={assets}
            editorRef={editorRef}
            forceUpdate={forceUpdateEditor} // 🔧 传入 forceUpdate 计数器，每次打开时强制同步
            showMentionDropdown={true} // 🔧 全屏编辑器需要显示 @ 下拉
            style={{ flex: 1, fontSize, fontWeight, lineHeight: 1.8 }}
          />
        </div>
      </div>
    </div>,
    document.body
  );
}

// ══════════════════════════════════════════════════════════════
// 主组件
// ══════════════════════════════════════════════════════════════
export default function StoryboardEditor({ data, handleUpdate, nodeId }) {
  const { projectId } = useParams();   // 当前图板 ID（用于持久化分镜大师任务）
  const basePrompts = data.basePrompts || [];
  const gridCells = data.gridCells || [];
  const gridType = data.gridType || 9;
  const cols = GRID_COLS[gridType] || 3;
  // 比例联动：从模型参数中读取 aspect_ratio / size，回退 1:1
  const aspectRatio = data.params?.aspect_ratio || data.params?.size || '1:1';

  // 🆕 合并所有素材（用于富文本编辑器的 mention 功能）
  // ⚠️ 必须用 normalizeAsset 规范化，确保 mini_thumbnail_url 转为 miniThumbnailUrl
  const allAssets = [
    ...(data.refFiles || []).map(normalizeAsset),
    ...(data.globalAssetsRef?.current || []).map(normalizeAsset),
  ];

  // 从 data 中读取 parseText，如果不存在则为空字符串
  const [parseText, setParseText] = useState(normalizeText(data.parseText || '')); // 🆕 初始值也规范化
  const [isFilled, setIsFilled] = useState(false); // 标记文本是否已填充（用于灰色显示）
  const [editing, setEditing] = useState(null); // { type: 'base'|'cell', id, name, text } | null
  const [gridMenuOpen, setGridMenuOpen] = useState(false); // 宫格选择上拉菜单
  const [polishMenuOpen, setPolishMenuOpen] = useState(false); // 分镜大师模型上拉框
  // 🆕 @ 提及功能状态
  const [mentionMenuOpen, setMentionMenuOpen] = useState(false); // @ 下拉菜单是否打开
  const [mentionSearch, setMentionSearch] = useState(''); // @ 后面的搜索词
  const [mentionCursorPos, setMentionCursorPos] = useState(0); // @ 的位置
  const mentionMenuRef = useRef(null); // @ 下拉菜单容器
  // ── 分镜大师状态 ──────────────────────────────────────────────────────────
  // 已改为「后台任务」模式：状态来自节点 data（polishing / polishStartTime /
  // polishStatus / polishError），刷新/折叠/切标签/关软件都不影响，回来能恢复。
  // isPolishing 直接由 data.polishing 派生，不再用本地 SSE 流。
  const isPolishing = !!data.polishing;
  const polishStatus = data.polishStatus || '';
  const [polishError, setPolishError] = useState('');
  const [polishModels, setPolishModels] = useState([]);
  const [triggerIcon, setTriggerIcon] = useState('');  // 分镜大师按钮的自定义图标（全局，留空用内置魔棒）
  const [fillIcon, setFillIcon] = useState('');         // 填充按钮的自定义图标（全局，留空用内置星形）
  const [polishElapsed, setPolishElapsed] = useState(0);   // 已用时（秒），从 polishStartTime 计算
  const [polishLogoError, setPolishLogoError] = useState(false); // 遮罩里模型 logo 加载失败 → 降级魔棒图标
  const [forceParseTextUpdate, setForceParseTextUpdate] = useState(false); // 🆕 强制更新右侧文本框
  const [submitting, setSubmitting] = useState(false);     // 提交请求中（防抖，仅本地短暂）
  const [matching, setMatching] = useState(false);         // 🆕 匹配资产阶段（本地，POST 前）
  const [matchStatus, setMatchStatus] = useState('');      // 🆕 匹配阶段文字（"正在匹配资产…" / "已匹配 N 个资产"）
  const parseTextareaRef = useRef(null); // 保留引用（兼容性）

  const gridMenuRef = useRef(null);    // 宫格选择上拉框容器（点画布空白处收起）
  const polishMenuRef = useRef(null);  // 分镜大师模型上拉框容器（点画布空白处收起）

  // 点击菜单容器外（含画布空白处）收起上拉框。
  // 原来用 position:fixed;inset:0 遮罩兜底，但节点在 React Flow 变换后的视口内，
  // 遮罩的包含块变成节点本身而非整屏，点画布空白处捕捉不到 → 改用文档级 mousedown 判断。
  useEffect(() => {
    if (!gridMenuOpen && !polishMenuOpen && !mentionMenuOpen) return;
    const onDown = (e) => {
      if (gridMenuOpen && gridMenuRef.current && !gridMenuRef.current.contains(e.target)) setGridMenuOpen(false);
      if (polishMenuOpen && polishMenuRef.current && !polishMenuRef.current.contains(e.target)) setPolishMenuOpen(false);
      if (mentionMenuOpen && mentionMenuRef.current && !mentionMenuRef.current.contains(e.target)) setMentionMenuOpen(false);
    };
    // 捕获阶段：React Flow 画布 pane 会在 mousedown 目标阶段 stopPropagation（d3-drag 平移），
    // 冒泡阶段的 document 监听收不到画布空白处的点击，必须用 capture=true 抢在前面。
    document.addEventListener('mousedown', onDown, true);
    return () => document.removeEventListener('mousedown', onDown, true);
  }, [gridMenuOpen, polishMenuOpen, mentionMenuOpen]);

  // 计时：从 data.polishStartTime 每秒计算 elapsed（跨刷新/设备仍有效）
  useEffect(() => {
    if (!data.polishing || !data.polishStartTime) {
      setPolishElapsed(0);
      return;
    }
    const tick = () => setPolishElapsed(Math.max(0, Math.round((Date.now() - data.polishStartTime) / 1000)));
    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [data.polishing, data.polishStartTime]);

  // 🔗 无缝接力：后端把 polishing 置 true（WebSocket 回流）后，才收起本地「匹配资产」遮罩，
  // 交给 polishing 遮罩接着显示。避免"匹配完成清遮罩 → WS 还没到"之间出现空白，
  // 让客户误以为流程已经结束。
  useEffect(() => {
    if (isPolishing) {
      setMatching(false);
      setMatchStatus('');
    }
  }, [isPolishing]);

  // 后台任务失败时，data.polishError 回流 → 显示 5 秒后清除
  useEffect(() => {
    if (data.polishError) {
      setPolishError(data.polishError);
      const t = setTimeout(() => {
        setPolishError('');
        handleUpdate({ polishError: '' }); // 同时清除 data.polishError
      }, 5000);
      return () => clearTimeout(t);
    } else {
      // data.polishError 被外部清空时，同步清除本地 state
      setPolishError('');
    }
  }, [data.polishError, handleUpdate]);

  // 检测分镜大师可用模型（「分镜大师设定」里勾选的语言模型）
  useEffect(() => {
    const checkPolish = () => {
      api.get('/api/ai/storyboard/models').then(data => {
        setPolishModels(Array.isArray(data.models) ? data.models : []);
        setTriggerIcon(data.triggerIcon || '');
        setFillIcon(data.fillIcon || '');
      }).catch(() => {});
    };
    checkPolish();
    window.addEventListener('modelsUpdated', checkPolish);
    return () => window.removeEventListener('modelsUpdated', checkPolish);
  }, []);

  // 同步 data.parseText 到本地状态（当外部数据变化时）
  React.useEffect(() => {
    if (data.parseText !== undefined) {
      setParseText(normalizeText(data.parseText)); // 🆕 规范化从外部同步的文本
    }
  }, [data.parseText]);

  // 更新 parseText 的辅助函数：同时更新本地状态和持久化到 data
  const updateParseText = useCallback((newText, newSlateValue, fromFullscreen = false) => {
    setParseText(newText);
    const updates = { parseText: newText };
    if (newSlateValue !== undefined) {
      updates.parseTextSlateValue = newSlateValue;
    }
    handleUpdate(updates);
    // 🆕 只有全屏编辑器保存时才触发强制更新（避免右侧输入框每次输入都重置）
    if (fromFullscreen) {
      setForceParseTextUpdate(true);
      setTimeout(() => setForceParseTextUpdate(false), 100); // 100ms 后复位
    }
  }, [handleUpdate]);

  // 📝 分镜大师处理（后台任务模式）
  // 不再在前端持有 SSE 流。改为：POST 提交任务 → 后端立即标记 polishing + 后台跑，
  // 进度/结果经 WebSocket node-updated 回流到 data.parseText / data.polishing。
  // 因此刷新、折叠控制台、切标签页、甚至关软件再回来，生成都持续且能恢复。
  const handlePolish = useCallback(async (dbId, modelId) => {
    const text = (parseText || '').trim();
    if (!text || isPolishing || submitting) return;
    if (!projectId || !nodeId) {
      handleUpdate({ polishError: '无法定位当前分镜节点，请先保存后重试' });
      return;
    }
    setSubmitting(true);

    // 🆕 第一步：匹配资产 —— 检索正文关键词，命中素材库标题就在上游弹节点
    setMatching(true);
    setMatchStatus('正在匹配资产…');
    const pool = [
      ...(data.globalAssetsRef?.current || []),
      ...(data.publicAssetsRef?.current || []),
    ];
    // 取首个 http(s) URL；归一化指纹（去协议/去 query），用于跨库去重与同图比对
    const getHttpUrl = f => [f?.url, f?.localCacheUrl].find(u => u && /^https?:\/\//.test(u));
    const normUrl = (u) => (typeof u === 'string' ? u.replace(/^https?:\/\//, '').split('?')[0].toLowerCase() : '');
    // 资产内容指纹：图片优先归一化 URL、缺失退回标题；文本用文本内容、缺失退回标题
    const assetFingerprint = (a) => {
      if ((a.type || 'image') === 'text') {
        const t = (a.text || a.url || '').trim();
        return t ? `t:${t}` : `n:${(a.title || '').trim().toLowerCase()}`;
      }
      const u = normUrl(getHttpUrl(a));
      return u ? `u:${u}` : `n:${(a.title || '').trim().toLowerCase()}`;
    };

    // 🔎 先盘点「本节点已连的上游内容」，避免重复匹配/重复弹节点（用户诉求 ③）
    //   - mentionAssetUrl：本节点 mention 图 → 当前 URL（压缩后这里已是新图，复用它而非库里旧大图）
    //   - manualImageFps：手动连线等非 mention 图的 URL 指纹 → 命中则跳过不再弹
    //   - upstreamTextFps：上游已有文本指纹 → 命中则跳过
    const mentionAssetUrl = new Map();
    const manualImageFps = new Set();
    (data.refFiles || []).filter(f => (f.type || 'image') === 'image').forEach(f => {
      const id = String(f.id || '');
      const u = getHttpUrl(f);
      if (id.startsWith('mention-') && id.endsWith(`-for-${nodeId}`)) {
        const aid = id.replace('mention-', '').replace(`-for-${nodeId}`, '');
        if (u) mentionAssetUrl.set(aid, u);
      } else if (u) {
        manualImageFps.add(`u:${normUrl(u)}`);
      }
    });
    const upstreamTextFps = new Set((data.refTexts || []).map(t => {
      const txt = (t.text || '').trim();
      return txt ? `t:${txt}` : `n:${(t.title || '').trim().toLowerCase()}`;
    }));

    // 同素材的「标题+类型」身份键：后台/项目库两份记录 OSS URL 常不同（独立上传或跨 bucket），
    // 单靠 URL 指纹去不掉重（详见 assetFingerprint）。匹配本就按标题命中，同名同类型即同素材，
    // 故再补一条标题去重；pool 后台在前，first-seen 恰好让「能下载的后台份」胜出，项目库跨 bucket 坏链被丢弃。
    const titleTypeKey = (a) => `tt:${a.type || 'image'}:${(a.title || '').trim().toLowerCase()}`;

    // 🔧 优先从 Slate JSON 里提取 mention 节点引用的资产（用户用 @ 选的），避免纯文本匹配遗漏
    const mentionTitles = new Set();
    if (data.parseTextSlateValue) {
      try {
        const extractMentions = (nodes) => {
          for (const node of nodes || []) {
            if (node.type === 'mention' && node.title) {
              mentionTitles.add(node.title.trim().toLowerCase());
            }
            if (node.children) {
              extractMentions(node.children);
            }
          }
        };
        extractMentions(data.parseTextSlateValue);
      } catch (e) {
        console.warn('[Slate] 提取 mention 失败:', e);
      }
    }

    // 匹配资产：① mention 节点的标题精确匹配，② 纯文本里的标题模糊匹配
    const lowerText = text.toLowerCase();
    const seen = new Set();
    const matched = [];

    for (const a of pool) {
      const type = a.type || 'image';
      if (type !== 'image' && type !== 'text') continue; // ① 仅匹配图片/文本，视频音频不参与
      const title = (a.title || '').trim();
      if (title.length < 2) continue;                    // 短名门槛：<2 字不匹配
      const titleLower = title.toLowerCase();
      // 🔧 优先匹配 mention 节点里的标题（精确），其次纯文本里的标题（模糊）
      const isMatch = mentionTitles.has(titleLower) || lowerText.includes(titleLower);

      if (!isMatch) continue;
      const fp = assetFingerprint(a);
      const tk = titleTypeKey(a);
      if (seen.has(fp) || seen.has(tk)) continue;        // ② 按内容指纹 + 标题身份去重：跨后台库/项目库同素材只取一次
      seen.add(fp);
      seen.add(tk);
      matched.push(a);
    }

    // 媒体素材（仅图片）走 onMentionSync —— 自动增删/排布/连边/写 refFiles
    const mediaMatched = [];
    matched.filter(a => (a.type || 'image') === 'image').forEach(a => {
      const aid = String(a.id);
      if (mentionAssetUrl.has(aid)) {
        // 已是本节点 mention：已经连上了，跳过不再添加（避免重复创建节点）
      } else if (manualImageFps.has(assetFingerprint(a))) {
        // 上游已手动连了同一张图：跳过，不重复弹 mention（其 URL 已在参考图里）
      } else {
        // 全新资产：添加到匹配列表，等待创建 mention 节点
        mediaMatched.push(a);
      }
    });
    data.onMentionSync?.(mediaMatched, nodeId);
    // 文本素材（咒语）走 onSlashAssetSpawn —— 上游已有同文本则跳过，避免重复建节点
    const textMatched = matched.filter(a => a.type === 'text').filter(a => {
      const txt = (a.text || a.url || '').trim();
      const fp = txt ? `t:${txt}` : `n:${(a.title || '').trim().toLowerCase()}`;
      return !upstreamTextFps.has(fp);
    });
    textMatched.forEach(a => data.onSlashAssetSpawn?.(a, nodeId));

    // 让用户看到「正在匹配资产」+ 节点弹出（约 500ms），再显示匹配结果数（约 700ms）
    await new Promise(r => setTimeout(r, 500));
    setMatchStatus(`已匹配 ${matched.length} 个资产`);
    await new Promise(r => setTimeout(r, 700));
    // ⚠️ 不在此清除 matching：遮罩保持连续，转入「正在唤起模型…」，一直亮到后端
    //    polishing 经 WebSocket 回流接管（见上方 isPolishing 接力 effect）
    setMatchStatus('正在唤起模型…');

    // 🖼️ 收集参考图：已有 refFiles 的图 + 本次匹配到的素材图（直接从匹配对象算，不等 refFiles 异步回流）
    // 不再限定 Ark —— 全部传给后端，由后端按模型 supportsImages 决定是否真带图（前向兼容将来的识图模型）
    // 上游已有图（含压缩后的新图）优先并入；库里同图（同 URL 指纹）不再重复带，避免又拉回大图。
    const imageFpSeen = new Set();
    const imageUrls = [];
    const pushImage = (u) => {
      if (!u) return;
      const fp = normUrl(u);
      if (imageFpSeen.has(fp)) return;
      imageFpSeen.add(fp);
      imageUrls.push(u);
    };
    // 先放上游已连的图（压缩后这里是新图 URL），再放本次匹配到的库素材图
    (data.refFiles || []).filter(f => (f.type || 'image') === 'image').forEach(f => pushImage(getHttpUrl(f)));
    mediaMatched.forEach(a => pushImage(getHttpUrl(a)));

    // 📝 收集参考文本：节点已有 refTexts + 本轮匹配到的文本素材（按文本内容去重）
    // 分镜大师是语言模型，文本内容附加给它能读；图片声音不支持时后端自动不用
    const textMap = new Map();
    (data.refTexts || []).forEach(t => { if (t.text) textMap.set(t.text, { title: t.title || '', text: t.text }); });
    textMatched.forEach(a => {
      const txt = a.text || a.url || '';
      if (txt) textMap.set(txt, { title: a.title || '', text: txt });
    });
    const refTexts = Array.from(textMap.values());

    try {
      // 后端会立即返回，真正生成在后台跑；data.polishing 由 WebSocket 推送置为 true
      // 🫧 可引用的素材名：本轮匹配到的图片素材 + 上游已连的参考图，让模型输出 @素材名，右侧文本框据此自动显示气泡
      const assetTitles = Array.from(new Set([
        ...matched.filter(a => (a.type || 'image') === 'image').map(a => (a.title || '').trim()),
        ...(data.refFiles || []).filter(f => (f.type || 'image') === 'image' && !f.isStoryboardDraft)
          .map(f => (f.title || '').trim()),
      ].filter(t => t.length >= 2)));
      await api.post('/api/ai/storyboard-polish', { text, gridType, dbId, imageUrls, refTexts, assetTitles, projectId, nodeId });
      // 成功：不清 matching，遮罩连续亮到 polishing 接管（由 isPolishing 接力 effect 收起）。
      // 兜底：万一 polishing 的 WebSocket 没回来，10 秒后自动收起，避免遮罩永久卡住。
      setTimeout(() => setMatching(false), 10000);
    } catch (e) {
      console.error('分镜大师提交失败:', e);
      // 透出后端返回的具体原因（如"未配置分镜大师工具箱"），而非笼统的失败提示
      handleUpdate({ polishError: e.message || '分镜大师请求失败，请检查 API 设置中的工具箱配置' });
      setMatching(false);   // 出错才收遮罩
      setMatchStatus('');
    } finally {
      setSubmitting(false);
    }
  }, [parseText, isPolishing, submitting, projectId, nodeId, gridType, data.refFiles, data.refTexts, data.globalAssetsRef, data.publicAssetsRef, data.onMentionSync, data.onSlashAssetSpawn]);

  // 基础提示词：增/改/删
  const handleAddBase = useCallback(() => {
    const newBlock = { id: genId(), name: '提示词', text: '' };
    handleUpdate({ basePrompts: [...basePrompts, newBlock] });
    setEditing({ type: 'base', id: newBlock.id, name: newBlock.name, text: newBlock.text });
  }, [basePrompts, handleUpdate]);

  const handleSaveBase = useCallback((id, name, text) => {
    const next = basePrompts.map(b => b.id === id ? { ...b, name, text } : b);
    handleUpdate({ basePrompts: next });
  }, [basePrompts, handleUpdate]);

  const handleDeleteBase = useCallback((id) => {
    handleUpdate({ basePrompts: basePrompts.filter(b => b.id !== id) });
  }, [basePrompts, handleUpdate]);

  // 宫格：保存单格
  const handleSaveCell = useCallback((index, name, text) => {
    const cells = Array.from({ length: gridType }, (_, i) => {
      const old = gridCells[i] || { id: genId(), name: `第${i + 1}镜`, timeLabel: '', prompt: '' };
      if (i === index) return { ...old, name: name || `第${i + 1}镜`, prompt: text };
      return old;
    });
    handleUpdate({ gridCells: cells });

    // 同步更新右侧文本框：将修改后的宫格内容转回文本
    const newText = gridCellsToText(cells);
    updateParseText(newText);
  }, [gridCells, gridType, handleUpdate, updateParseText]);

  // 🔒 宫格锁定：只写 cell.locked 布尔值，绝不碰 cell.prompt（右侧文本框 / 视频提示词保持原词）。
  //   锁定后，下次生成时（有上一轮成品图的前提下）该格会被替换成「与参考图对应格保持一致」，
  //   并把上一轮整图多喂一张给模型 —— 这两件事都在 useNodeActions 生成时按 data.output 现取现用，
  //   所以这里不需要提前抓快照，也不需要代号。
  const handleToggleLock = useCallback((index) => {
    const cells = Array.from({ length: gridType }, (_, i) => (
      gridCells[i] || { id: genId(), name: `第${i + 1}镜`, timeLabel: '', prompt: '' }
    ));
    const target = cells[index];
    if (!target.locked && !data.output) return;   // 没成品图不能锁（按钮已禁用，双保险）
    cells[index] = { ...target, locked: !target.locked };
    handleUpdate({ gridCells: cells });
  }, [gridCells, gridType, data.output, handleUpdate]);

  // 解析填充核心：把给定文本解析成宫格并写回（手动按钮 / 生成完成自动填充共用）
  // 传显式 text 而非读闭包 parseText —— 自动填充时可直接用刚回流的 data.parseText，避开状态时序坑
  // @param {string} text 要解析的文本
  // @param {boolean} isAutoFill 是否为自动填充（生成完成后）
  // @param {boolean} syncBackToText 是否回写右侧文本（手动填充、编辑格子时需要；延迟自动填充时不需要）
  // @param {boolean} showErrorIfEmpty 是否在解析失败时显示错误提示（只有手动点填充按钮时才显示）
  const fillCellsFromText = useCallback((text, isAutoFill = false, syncBackToText = true, showErrorIfEmpty = false) => {
    const src = (text || '').trim();
    if (!src) return;
    const parsed = parseStoryboardText(src, gridType);

    // 格式检测：只在手动点击填充按钮时提示（showErrorIfEmpty=true）
    if (parsed.length === 0 && showErrorIfEmpty) {
      handleUpdate({
        polishError: '❌ 未识别到分镜格式。请确保每个分镜以"第X镜"、"X镜"或"镜头X"开头，独占一行。'
      });
      // 不需要手动清除，useEffect 会在5秒后自动清除
      return;
    }

    // 合并：保留用户已经手动编辑的格子（解析超出 N 段时不动后面）
    const cells = Array.from({ length: gridType }, (_, i) => {
      const old = gridCells[i] || { id: genId(), name: `第${i + 1}镜`, timeLabel: '', prompt: '' };
      const p = parsed[i];
      if (!p) return old; // 保持
      return {
        id: old.id,
        name: old.name || p.name,    // 保留用户改过的名字
        timeLabel: p.timeLabel,
        prompt: p.prompt,
        locked: old.locked,          // 🔒 重新填充不丢锁定态
      };
    });
    handleUpdate({ gridCells: cells });

    // 🆕 是否回写右侧文本
    if (syncBackToText) {
      if (!isAutoFill) {
        // 手动填充时，整理文本（跳过空格子，去掉无效行）
        const cleanedText = gridCellsToText(cells, false);
        updateParseText(cleanedText);
      }
      // 自动填充时（生成完成）保留原始生成的完整文本，不回写
    }

    // 🆕 只有手动填充或生成完成时才标记为已填充；延迟自动填充不触发
    if (syncBackToText) {
      setIsFilled(true);
    }
  }, [gridType, gridCells, handleUpdate, updateParseText]);

  // 手动「填充」按钮：解析本地文本框内容 + 匹配资产弹出上游节点
  const handleParseFill = useCallback(async () => {
    fillCellsFromText(parseText, false, true, true); // 🔑 手动填充时才检测格式

    // 🔧 填充后也执行资产匹配逻辑（与分镜大师按钮一致）
    const text = (parseText || '').trim();
    if (!text) return;

    // 🆕 匹配资产 —— 检索正文关键词，命中素材库标题就在上游弹节点
    const pool = [
      ...(data.globalAssetsRef?.current || []),
      ...(data.publicAssetsRef?.current || []),
    ];
    // 取首个 http(s) URL；归一化指纹（去协议/去 query），用于跨库去重与同图比对
    const getHttpUrl = f => [f?.url, f?.localCacheUrl].find(u => u && /^https?:\/\//.test(u));
    const normUrl = (u) => (typeof u === 'string' ? u.replace(/^https?:\/\//, '').split('?')[0].toLowerCase() : '');
    // 资产内容指纹：图片优先归一化 URL、缺失退回标题；文本用文本内容、缺失退回标题
    const assetFingerprint = (a) => {
      if ((a.type || 'image') === 'text') {
        const t = (a.text || a.url || '').trim();
        return t ? `t:${t}` : `n:${(a.title || '').trim().toLowerCase()}`;
      }
      const u = normUrl(getHttpUrl(a));
      return u ? `u:${u}` : `n:${(a.title || '').trim().toLowerCase()}`;
    };

    // 🔎 先盘点「本节点已连的上游内容」，避免重复匹配/重复弹节点
    const mentionAssetUrl = new Map();
    const manualImageFps = new Set();
    (data.refFiles || []).filter(f => (f.type || 'image') === 'image').forEach(f => {
      const id = String(f.id || '');
      const u = getHttpUrl(f);
      if (id.startsWith('mention-') && id.endsWith(`-for-${nodeId}`)) {
        const aid = id.replace('mention-', '').replace(`-for-${nodeId}`, '');
        if (u) mentionAssetUrl.set(aid, u);
      } else if (u) {
        manualImageFps.add(`u:${normUrl(u)}`);
      }
    });
    const upstreamTextFps = new Set((data.refTexts || []).map(t => {
      const txt = (t.text || '').trim();
      return txt ? `t:${txt}` : `n:${(t.title || '').trim().toLowerCase()}`;
    }));

    // 同素材的「标题+类型」身份键：后台/项目库两份记录 OSS URL 常不同
    const titleTypeKey = (a) => `tt:${a.type || 'image'}:${(a.title || '').trim().toLowerCase()}`;

    const lowerText = text.toLowerCase();
    const seen = new Set();
    const matched = [];

    for (const a of pool) {
      const type = a.type || 'image';
      if (type !== 'image' && type !== 'text') continue; // ① 仅匹配图片/文本，视频音频不参与
      const title = (a.title || '').trim();
      if (title.length < 2) continue;                    // 短名门槛：<2 字不匹配
      const titleLower = title.toLowerCase();
      const isMatch = lowerText.includes(titleLower);
      if (!isMatch) continue;
      const fp = assetFingerprint(a);
      const tk = titleTypeKey(a);
      if (seen.has(fp) || seen.has(tk)) continue;        // ② 按内容指纹 + 标题身份去重
      seen.add(fp);
      seen.add(tk);
      matched.push(a);
    }

    // 媒体素材（仅图片）走 onMentionSync —— 自动增删/排布/连边/写 refFiles
    const mediaMatched = [];
    matched.filter(a => (a.type || 'image') === 'image').forEach(a => {
      const aid = String(a.id);
      if (mentionAssetUrl.has(aid)) {
        // 已是本节点 mention：已经连上了，跳过不再添加（避免重复创建节点）
      } else if (manualImageFps.has(assetFingerprint(a))) {
        // 上游已手动连了同一张图：跳过，不重复弹 mention（其 URL 已在参考图里）
      } else {
        // 全新资产：添加到匹配列表，等待创建 mention 节点
        mediaMatched.push(a);
      }
    });
    data.onMentionSync?.(mediaMatched, nodeId);

    // 文本素材（咒语）走 onSlashAssetSpawn —— 上游已有同文本则跳过，避免重复建节点
    const textMatched = matched.filter(a => a.type === 'text').filter(a => {
      const txt = (a.text || a.url || '').trim();
      const fp = txt ? `t:${txt}` : `n:${(a.title || '').trim().toLowerCase()}`;
      return !upstreamTextFps.has(fp);
    });
    textMatched.forEach(a => data.onSlashAssetSpawn?.(a, nodeId));
  }, [fillCellsFromText, parseText, data, nodeId]);

  // 🪄 生成完成自动填充：监听 polishing 从 true→false 的跃迁，成功（无 polishError）时
  // 直接用刚回流的 data.parseText 解析进宫格。后端在同一条推送里下发 parseText+polishing:false，
  // 故此刻 data.parseText 已是最终完整文本，无需等本地 state 同步。手动填充按钮保留不变。
  // 注意：必须放在 fillCellsFromText 定义之后，否则依赖数组求值时触发 TDZ。
  const prevPolishingRef = useRef(!!data.polishing);
  useEffect(() => {
    const prev = prevPolishingRef.current;
    const now = !!data.polishing;
    prevPolishingRef.current = now;
    // 仅在「刚从生成中变为完成」且非失败、有内容时自动填充
    if (prev && !now && !data.polishError && (data.parseText || '').trim()) {
      fillCellsFromText(data.parseText, true); // 🆕 传入 isAutoFill=true，保留原始生成文本
    }
  }, [data.polishing, data.polishError, data.parseText, fillCellsFromText]);

  // 渲染单格
  const renderedCells = useMemo(() => {
    return Array.from({ length: gridType }, (_, i) => {
      const cell = gridCells[i] || { id: `c${i}`, name: `第${i + 1}镜`, timeLabel: '', prompt: '' };
      return cell;
    });
  }, [gridCells, gridType]);

  // 🔧 计算左侧宫格的总高度（用于右侧输入框高度对齐）
  const gridHeight = useMemo(() => {
    const cellWidth = cellWidthForGrid(gridType);
    const ratio = parseRatio(aspectRatio);
    const [w, h] = ratio.split('/').map(v => parseFloat(v.trim()));
    const cellHeight = cellWidth * (h / w);
    const rows = Math.sqrt(gridType);
    const gap = 2;
    return cellHeight * rows + gap * (rows - 1); // 不包括 border，由外层容器处理
  }, [gridType, aspectRatio]);

  return (
    <div className="nodrag nowheel" style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: 0, position: 'relative' }}>

      {/* ─── 基础提示词区 ─── */}
      {/* 基础提示词区已移除 */}

      {/* ─── 宫格区 + 分镜文本解析区（两列并置，紧凑，无标头）─── */}
      <div style={{ display: 'flex', gap: 6, alignItems: 'stretch' }}>
        {/* 左列：宫格区（短边 100px，无圆角，紧凑） */}
        <section style={{ flexShrink: 0 }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${cols}, ${cellWidthForGrid(gridType)}px)`,
            gap: 2,
          }}>
            {renderedCells.map((cell, i) => {
              const locked = !!cell.locked;
              const canLock = !!data.output;   // 有成品图才能锁
              // 锁定视觉：灰黑格 + 压暗文字；未锁定沿用原「有内容红 / 空白虚线」
              const bg = locked ? 'rgba(20,20,22,0.92)' : (cell.prompt ? 'var(--sb-cell-bg)' : 'var(--sb-cell-empty-bg)');
              const bd = locked ? '1px solid rgba(251,191,36,0.4)' : (cell.prompt ? '1px solid var(--sb-cell-border)' : '1px dashed var(--border-strong)');
              const bdHover = locked ? 'rgba(251,191,36,0.65)' : (cell.prompt ? 'var(--sb-cell-hover)' : '#3b82f6');
              return (
              <div
                key={cell.id || i}
                onClick={() => { if (locked) return; setEditing({ type: 'cell', index: i, name: cell.name, timeLabel: cell.timeLabel, text: cell.prompt }); }}
                style={{
                  position: 'relative',
                  aspectRatio: parseRatio(aspectRatio),
                  background: bg,
                  border: bd,
                  borderRadius: 0,
                  padding: 4,
                  cursor: locked ? 'default' : 'pointer',
                  overflow: 'hidden',
                  transition: 'all 0.15s',
                  display: 'flex', flexDirection: 'column',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = bdHover; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = locked ? 'rgba(251,191,36,0.4)' : (cell.prompt ? 'var(--sb-cell-border)' : 'var(--border-strong)'); }}
              >
                <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', marginBottom: 2 }}>
                  <span style={{ fontSize: 10, fontWeight: 600, color: locked ? '#9ca3af' : (cell.prompt ? 'var(--sb-cell-title)' : 'var(--text-secondary)'), overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {cell.name || `第${i + 1}镜`}{cell.timeLabel ? ` ${cell.timeLabel}` : ''}
                  </span>
                </div>
                <div style={{
                  flex: 1, fontSize: 10, lineHeight: 1.3,
                  color: locked ? 'rgba(255,255,255,0.32)' : 'var(--sb-cell-body)',
                  overflow: 'hidden', textOverflow: 'ellipsis',
                  display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical',
                  whiteSpace: 'pre-wrap',
                }}>
                  {cell.prompt ? withBlankLines(cell.prompt) : <span style={{ color: 'var(--sb-cell-placeholder)', fontStyle: 'italic' }}>编辑…</span>}
                </div>

                {/* 🔒 锁定按钮：放大按钮左侧。锁定后重生成时该格保持不变 */}
                <div
                  title={!canLock ? '先生成一次再锁定' : (locked ? '解锁此格' : '锁定此格（重生成时保持不变）')}
                  onClick={(e) => { e.stopPropagation(); if (!canLock) return; handleToggleLock(i); }}
                  style={{ position: 'absolute', right: 32, bottom: 6, width: 22, height: 22, borderRadius: 4,
                    background: locked ? 'rgba(251,191,36,0.22)' : 'var(--sb-btn-bg)',
                    border: '1px solid ' + (locked ? 'rgba(251,191,36,0.55)' : 'var(--sb-btn-border)'),
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: canLock ? 'pointer' : 'not-allowed', opacity: canLock ? 1 : 0.4, transition: 'all 0.12s' }}
                  onMouseEnter={e => { if (!canLock) return; e.currentTarget.style.transform = 'scale(1.03)'; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
                >
                  {locked
                    ? <Lock size={12} color="#fbbf24" strokeWidth={2} />
                    : <Unlock size={12} color="var(--sb-btn-icon)" strokeWidth={2} />}
                </div>

                {/* 小放大按钮：右下角 */}
                <div
                  onClick={(e) => { e.stopPropagation(); if (locked) return; setEditing({ type: 'cell', index: i, name: cell.name, timeLabel: cell.timeLabel, text: cell.prompt }); }}
                  style={{ position: 'absolute', right: 6, bottom: 6, width: 22, height: 22, borderRadius: 4, background: 'var(--sb-btn-bg)', border: '1px solid var(--sb-btn-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: locked ? 'not-allowed' : 'pointer', opacity: locked ? 0.4 : 1, transition: 'all 0.12s' }}
                  onMouseEnter={e => { if (locked) return; e.currentTarget.style.background = 'var(--sb-btn-hover-bg)'; e.currentTarget.style.borderColor = 'var(--sb-btn-hover-border)'; e.currentTarget.style.transform = 'scale(1.03)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'var(--sb-btn-bg)'; e.currentTarget.style.borderColor = 'var(--sb-btn-border)'; e.currentTarget.style.transform = 'scale(1)'; }}
                >
                  <Maximize2 size={12} color="var(--sb-btn-icon)" strokeWidth={2} />
                </div>
              </div>
              );
            })}
          </div>
        </section>

        {/* 右列：文本解析区（高度与宫格拉齐，按钮浮动在右下角） */}
        <section style={{ flex: 1, minWidth: 240, position: 'relative', display: 'flex', isolation: 'isolate', height: gridHeight }}>
          {/* 等待遮罩：匹配资产阶段（matching，本地）或分镜大师生成中（data.polishing）即显示。
              顶部显示模型 logo + 模型名（对齐图像节点），下接放大的转圈 + 阶段文字 + 已用时 */}
          {(isPolishing || matching) && (
            <div style={{
              position: 'absolute', inset: 0, zIndex: 20,
              background: 'rgba(0,0,0,0.6)',
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              pointerEvents: 'all',
            }}>
              {/* 模型名 + logo（匹配阶段还没模型信息则不显示） */}
              {!matching && (data.polishModelName || data.polishModelLogo) && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 7, marginBottom: 18, maxWidth: 220,
                  fontSize: 14, color: 'rgba(255,255,255,0.85)', fontWeight: 600, letterSpacing: '0.4px',
                }}>
                  {data.polishModelLogo && !polishLogoError
                    ? <img src={data.polishModelLogo} alt="" style={{ width: 20, height: 20, objectFit: 'contain', borderRadius: 4, flexShrink: 0 }} onError={() => setPolishLogoError(true)} />
                    : <Wand2 size={18} color="#60a5fa" style={{ flexShrink: 0 }} />}
                  <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{data.polishModelName || '分镜大师'}</span>
                </div>
              )}
              {/* 转圈动画（放大） */}
              <div style={{ width: 60, height: 60, border: '3px solid rgba(255,255,255,0.12)', borderTop: '3px solid rgba(96,165,250,0.85)', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
              <span style={{ marginTop: 14, fontSize: 12, color: 'rgba(255,255,255,0.7)', letterSpacing: '0.4px' }}>{matching ? matchStatus : (polishStatus || '正在撰写…')}</span>
              {!matching && polishElapsed > 0 && (
                <span style={{ marginTop: 5, fontSize: 12, color: 'rgba(96,165,250,0.9)', fontWeight: 600, letterSpacing: '0.4px' }}>
                  已用时 {formatElapsed(polishElapsed)}
                </span>
              )}
            </div>
          )}
          {/* 🆕 Slate.js 富文本编辑器 */}
          <SlateStoryboardEditor
            editorRef={parseTextareaRef}
            value={parseText}
            slateValue={data.parseTextSlateValue} // 🆕 优先使用 Slate JSON
            forceUpdate={forceParseTextUpdate} // 🆕 全屏编辑器保存后强制更新
            showMentionDropdown={false} // 🆕 分镜大师右侧已有整个面板的下拉，不需要重复
            onChange={(e) => {
              if (isPolishing) return;
              updateParseText(e.target.value, e.target.slateValue); // 🆕 同时保存 slateValue
              if (isFilled) setIsFilled(false);
            }}
            onKeyDown={(e) => {
              // 检测 @ 输入
              if (e.key === '@') {
                setMentionSearch('');
                setMentionMenuOpen(true);
                return;
              }
              // ESC 关闭下拉菜单
              if (e.key === 'Escape' && mentionMenuOpen) {
                setMentionMenuOpen(false);
                return;
              }
            }}
            onFocus={() => {
              // 可选：焦点事件处理
            }}
            onBlur={() => {
              // 延迟关闭下拉菜单（确保点击素材有效）
              setTimeout(() => setMentionMenuOpen(false), 200);
            }}
            placeholder={'输入你的故事，我会按照左侧宫格数设定帮你撰写分镜。\n\n也可以直接粘贴已有分镜文本（每个分镜以"第X镜"、"X镜"或"镜头X"开头），点击下方【填充】按钮自动识别填入。\n\n分镜数量少于宫格数时，剩余格子生图时会自动黑屏。'}
            disabled={isPolishing}
            assets={allAssets}
            style={{
              flex: 1,
              width: '100%',
              boxSizing: 'border-box',
              padding: '8px 8px 8px 8px', // 🔧 不再在下方留 36px 空间给按钮，按钮浮动在上面
              fontSize: 10, lineHeight: 1.5,
              background: 'var(--bg-panel-2)',
              border: '1px solid var(--border-subtle)', borderRadius: 0,
              fontFamily: '"PingFang SC", "Microsoft YaHei", "Hiragino Sans GB", "Source Han Sans CN", "WenQuanYi Micro Hei", "Helvetica Neue", Helvetica, Arial, sans-serif',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              overflow: 'auto',
              color: isFilled ? 'var(--text-muted)' : 'var(--text-node-fg)',
            }}
          />
          {/* 错误提示：浮在文本框内、右下角按钮上方 */}
          {polishError && (
            <div style={{
              position: 'absolute', left: 6, right: 6, bottom: 42, zIndex: 15,
              padding: '6px 10px',
              background: 'rgba(239,68,68,0.18)', border: '1px solid rgba(239,68,68,0.45)',
              borderRadius: 6, fontSize: 11, color: '#f87171',
              lineHeight: 1.5, wordBreak: 'break-word', whiteSpace: 'normal',
              backdropFilter: 'blur(4px)',
            }}>
              ❌ {polishError}
            </div>
          )}
          {/* 右下角按钮区：图标化的 四个按钮（宫格选择 / 填充 / 语音输入 / 全屏编辑） */}
          <div style={{ position: 'absolute', right: 6, bottom: 6, display: 'flex', alignItems: 'center', gap: 6, zIndex: 10 }}>
            {/* helper: icon-only button matching NodeConsole style */}
            {/** Grid select（按钮 + 上拉菜单包在 relative 容器内，使菜单对齐按钮上方）*/}
            <div ref={gridMenuRef} style={{ position: 'relative', display: 'flex' }}>
              <QuickTooltip content="选择宫格数量" position="top" delay={200}>
                <div
                  onClick={() => setGridMenuOpen(o => !o)}
                  style={{
                    width: 28, height: 28, borderRadius: 6, background: 'var(--sb-btn-bg)',
                    border: '1px solid var(--sb-btn-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                    transition: 'all 0.15s', backdropFilter: 'blur(4px)'
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'var(--sb-btn-hover-bg)'; e.currentTarget.style.borderColor = 'var(--sb-btn-hover-border)'; e.currentTarget.style.transform = 'scale(1.05)'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'var(--sb-btn-bg)'; e.currentTarget.style.borderColor = 'var(--sb-btn-border)'; e.currentTarget.style.transform = 'scale(1)'; }}
                >
                  <LayoutGrid size={16} color="var(--sb-btn-icon)" />
                </div>
              </QuickTooltip>
              {gridMenuOpen && (
                <div style={{ position: 'absolute', left: '50%', bottom: 36, transform: 'translateX(-50%)', zIndex: 1002, display: 'flex', flexDirection: 'column', gap: 6, padding: 8, background: 'rgba(20,20,24,0.97)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 6 }}>
                  {GRID_OPTIONS.map(n => (
                    <div key={n} onClick={() => { handleUpdate({ gridType: n, gridCells: [] }); setGridMenuOpen(false); }}
                      style={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', borderRadius: 6, background: gridType === n ? '#3b82f6' : 'transparent', color: gridType === n ? '#fff' : '#cbd5e1', fontWeight: 700 }}>
                      {n}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/** Fill button */}
            <QuickTooltip content={parseText.trim() ? '填充' : '请先输入内容'} position="top" delay={200}>
              <div onClick={handleParseFill} style={{
                width: 28, height: 28, borderRadius: 6,
                background: 'var(--sb-btn-bg)',
                border: '1px solid var(--sb-btn-border)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: parseText.trim() ? 'pointer' : 'not-allowed',
                transition: 'all 0.15s', backdropFilter: 'blur(4px)',
                opacity: parseText.trim() ? 1 : 0.55,
              }}
                onMouseEnter={e => {
                  if (!parseText.trim()) return;
                  e.currentTarget.style.background = 'var(--sb-btn-hover-bg)';
                  e.currentTarget.style.borderColor = 'var(--sb-btn-hover-border)';
                  e.currentTarget.style.transform = 'scale(1.05)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = 'var(--sb-btn-bg)';
                  e.currentTarget.style.borderColor = 'var(--sb-btn-border)';
                  e.currentTarget.style.transform = 'scale(1)';
                }}
              >
                {fillIcon
                  ? <img src={fillIcon} alt="填充" style={{ width: 18, height: 18, objectFit: 'contain', borderRadius: 3, opacity: parseText.trim() ? 1 : 0.55 }} />
                  : <Sparkles size={14} color={parseText.trim() ? '#86efac' : 'var(--text-secondary)'} strokeWidth={2} />}
              </div>
            </QuickTooltip>

            {/** 分镜大师：单个按钮 + 上拉框（框内列出所有已配置模型，带定价，点选即触发润色）*/}
            {polishModels.length > 0 && (() => {
              // 触发按钮：生成中/提交中才禁用；没输入文字时仍可点开菜单浏览模型与定价
              const triggerDisabled = isPolishing || submitting;
              // 真正能否跑润色：还要求有输入文字
              const canPolish = !triggerDisabled && !!parseText.trim();
              const getHttpUrl = f => [f.url, f.localCacheUrl].find(u => u && /^https?:\/\//.test(u));
              const imgRefFiles = (data.refFiles || []).filter(f => f.type === 'image');
              const hasUpstreamImages = imgRefFiles.length > 0;
              const hasHttpImages = imgRefFiles.some(f => getHttpUrl(f));
              // 触发按钮的 tooltip：生成中显示状态，否则提示用途
              const triggerTooltip = isPolishing ? (polishStatus || '分镜大师处理中…')
                : '分镜大师（选择模型）';
              return (
              <div ref={polishMenuRef} style={{ position: 'relative', display: 'flex' }}>
                <QuickTooltip content={triggerTooltip} position="top" delay={200}>
                  <div onClick={() => { if (!triggerDisabled) setPolishMenuOpen(o => !o); }} style={{
                    width: 28, height: 28, borderRadius: 6,
                    background: isPolishing ? 'rgba(96,165,250,0.2)' : (polishMenuOpen ? 'rgba(96,165,250,0.18)' : 'var(--sb-btn-bg)'),
                    border: '1px solid ' + (isPolishing || polishMenuOpen ? 'rgba(96,165,250,0.4)' : 'var(--sb-btn-border)'),
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: triggerDisabled ? 'wait' : 'pointer',
                    transition: 'all 0.15s', backdropFilter: 'blur(4px)',
                    opacity: triggerDisabled ? 0.7 : 1,
                  }}
                    onMouseEnter={e => {
                      if (triggerDisabled) return;
                      e.currentTarget.style.background = 'rgba(96,165,250,0.18)';
                      e.currentTarget.style.borderColor = 'rgba(96,165,250,0.35)';
                      e.currentTarget.style.transform = 'scale(1.05)';
                    }}
                    onMouseLeave={e => {
                      if (isPolishing || polishMenuOpen) return;
                      e.currentTarget.style.background = 'var(--sb-btn-bg)';
                      e.currentTarget.style.borderColor = 'var(--sb-btn-border)';
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                  >
                    {isPolishing
                      ? <Loader2 size={14} color="#60a5fa" style={{ animation: 'spin 1s linear infinite' }} />
                      : triggerIcon
                        ? <img src={triggerIcon} alt="分镜大师" style={{ width: 20, height: 20, objectFit: 'contain', borderRadius: 3 }} />
                        : <Wand2 size={14} color="#60a5fa" strokeWidth={2} />}
                  </div>
                </QuickTooltip>

                {/* 上拉框：列出全部已配置模型，每项带名称 + 定价；点选即触发该模型润色 */}
                {polishMenuOpen && !triggerDisabled && (
                    <div style={{
                      position: 'absolute', right: 0, bottom: 36, zIndex: 1002, minWidth: 200, maxWidth: 280,
                      display: 'flex', flexDirection: 'column', gap: 2, padding: 6,
                      background: 'rgba(20,20,24,0.97)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8,
                      boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
                    }}>
                      <div style={{ fontSize: 10, color: '#64748b', padding: '2px 8px 4px', letterSpacing: '0.3px' }}>
                        {canPolish ? '选择分镜大师模型' : '请先在左侧输入分镜内容'}
                      </div>
                      {polishModels.map((m) => {
                        const canSeeImages = !!m.supportsVision;
                        const imgHint = !hasUpstreamImages ? ''
                          : !canSeeImages ? '该模型不支持参考图'
                          : !hasHttpImages ? '图片需上传云端才能参考'
                          : `将参考 ${imgRefFiles.length} 张图片`;
                        return (
                          <div key={m.id}
                            onClick={() => { if (canPolish) { setPolishMenuOpen(false); handlePolish(m.id, m.id); } }}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 8, padding: '8px 8px', borderRadius: 6,
                              cursor: canPolish ? 'pointer' : 'not-allowed', opacity: canPolish ? 1 : 0.5,
                              transition: 'background 0.12s',
                            }}
                            onMouseEnter={e => { if (canPolish) e.currentTarget.style.background = 'rgba(96,165,250,0.16)'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                          >
                            {m.logoUrl
                              ? <img src={m.logoUrl} alt="" style={{ width: 20, height: 20, objectFit: 'contain', borderRadius: 3, flexShrink: 0 }} />
                              : <Wand2 size={15} color="#60a5fa" style={{ flexShrink: 0 }} />}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 12.5, color: '#e2e8f0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name}</div>
                              {imgHint && <div style={{ fontSize: 10, color: '#94a3b8' }}>{imgHint}</div>}
                            </div>
                            {m.pricePer1kOutput > 0
                              ? <span style={{ fontSize: 10, color: 'var(--amber-accent)', flexShrink: 0 }}>{m.pricePer1kOutput}/千token</span>
                              : <span style={{ fontSize: 10, color: '#64748b', flexShrink: 0 }}>免费</span>}
                          </div>
                        );
                      })}
                    </div>
                )}
              </div>
              );
            })()}

            {/** Voice input button (use VoiceMicButton in floating small style) */}
            <QuickTooltip content="语音输入" position="top" delay={200}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <VoiceMicButton textareaRef={parseTextareaRef} currentText={parseText} onResult={(txt) => updateParseText(normalizeText(txt))} size={28} position="floating" />
              </div>
            </QuickTooltip>

            {/** Fullscreen editor: open FullscreenEditor for the whole parse textarea */}
            <QuickTooltip content="全屏编辑" position="top" delay={200}>
              <div
                onClick={() => setEditing({ type: 'parseText', index: -1, name: '分镜填充', text: parseText })}
                style={{ width: 28, height: 28, borderRadius: 6, background: 'var(--sb-btn-bg)', border: '1px solid var(--sb-btn-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'var(--sb-btn-hover-bg)'; e.currentTarget.style.borderColor = 'var(--sb-btn-hover-border)'; e.currentTarget.style.transform = 'scale(1.05)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'var(--sb-btn-bg)'; e.currentTarget.style.borderColor = 'var(--sb-btn-border)'; e.currentTarget.style.transform = 'scale(1)'; }}
              >
                <Maximize2 size={14} color="var(--sb-btn-icon)" strokeWidth={2} />
              </div>
            </QuickTooltip>
          </div>
        </section>

        {/* 🆕 @ 提及下拉菜单 - 放在右侧 section 外，覆盖整个右侧区域 */}
        {mentionMenuOpen && (
          <MentionDropdown
            query={mentionSearch || ''}
            assets={[
              ...(data.refFiles || []),
              ...(data.globalAssetsRef?.current || []),
            ]}
            title="素材库匹配"
            filterAsset={(asset, q) => {
              if (!asset || !asset.title) return false;
              const query = (q || '').toLowerCase();
              return asset.title.toLowerCase().includes(query);
            }}
            onSelect={(asset) => {
              if (!asset || !asset.title) return;

              // 使用 SlateStoryboardEditor 的 insertMention 方法
              if (parseTextareaRef.current?.insertMention) {
                parseTextareaRef.current.insertMention(asset);
              }

              setMentionMenuOpen(false);
            }}
            onClose={() => setMentionMenuOpen(false)}
          />
        )}
      </div>

      {/* ─── 全屏编辑器（基础卡片 / 宫格单格 / 右侧文本框共用） ─── */}
      <FullscreenEditor
        open={!!editing}
        title={
          editing?.type === 'parseText' ? '分镜填充编辑' :
          editing?.type === 'cell' ? `第${(editing.index || 0) + 1}镜${editing.timeLabel ? ` ${editing.timeLabel}` : ''}` :
          '编辑基础提示词'
        }
        name={editing?.name}
        text={editing?.text}
        assets={allAssets}
        onClose={() => setEditing(null)}
        onSave={(name, text, slateValue) => {
          if (editing.type === 'base') {
            handleSaveBase(editing.id, name, text);
          } else if (editing.type === 'cell') {
            handleSaveCell(editing.index, name, text);
          } else if (editing.type === 'parseText') {
            // 保存到右侧文本框并持久化（同时保存 slateValue）
            updateParseText(text, slateValue, true); // 🆕 fromFullscreen = true
            setIsFilled(false); // 清除已填充状态，恢复正常颜色
          }
        }}
      />
    </div>
  );
}
